import { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppBar, Toolbar, Typography, Button, Box, Container, Chip,
  Menu, MenuItem, ListItemIcon, Divider, IconButton, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArchiveIcon from '@mui/icons-material/Archive';
import SettingsIcon from '@mui/icons-material/Settings';
import TuneIcon from '@mui/icons-material/Tune';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AssessmentIcon from '@mui/icons-material/Assessment';
import InsightsIcon from '@mui/icons-material/Insights';
import LanguageIcon from '@mui/icons-material/Language';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BugReportIcon from '@mui/icons-material/BugReport';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import BugReportDialog from './BugReportDialog.js';
import DbEnvironmentBadge from './DbEnvironmentBadge.js';
import { useAuth } from '../hooks/useAuth.js';
import { UserRole } from '../../shared/types.js';
import { colors } from '../styles/theme.js';
import { confirmNavigation } from '../hooks/useUnsavedGuard.js';
import type { RootState, AppDispatch } from '../store/index.js';
import { fetchUiConfig } from '../store/uiConfigSlice.js';

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { user, hasRole, logout } = useAuth();

  // Globale Sichtbarkeit des (einfachen) Konfigurators — global im Store, damit ein Umschalten
  // im Admin sofort greift (ohne Seiten-Reload). Entwicklermodus-Nutzer sehen ihn immer.
  const configuratorVisible = useSelector((s: RootState) => s.uiConfig.configuratorVisible);
  useEffect(() => {
    if (user) dispatch(fetchUiConfig());
  }, [user, dispatch]);

  const showConfigurator = (configuratorVisible || user?.dev_mode === true)
    && hasRole([UserRole.DIGA, UserRole.ADMIN]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const closeMenu = () => setAnchorEl(null);

  // Produktbereiche: Domain Screener (Standard) und Watchdog. Der aktive Bereich ergibt sich
  // aus dem Pfad. Watchdog ist aktuell nur fuer Admins zugaenglich.
  const canSeeWatchdog = hasRole([UserRole.ADMIN]);
  const area: 'screener' | 'watchdog' = location.pathname.startsWith('/watchdog') ? 'watchdog' : 'screener';
  const areaLabel = area === 'watchdog' ? 'Watchdog' : 'Domain Screener';

  const [areaAnchorEl, setAreaAnchorEl] = useState<null | HTMLElement>(null);
  const areaMenuOpen = Boolean(areaAnchorEl);

  // Fehler melden: fuer jeden eingeloggten Nutzer, nicht nur fuer Admins.
  const [bugOpen, setBugOpen] = useState(false);

  /**
   * Jede Navigation fragt vorher nach: Seiten mit ungespeicherten Aenderungen
   * (aktuell die Watchdog-Domains im Expertenmodus) melden sich ueber
   * `registerUnsavedGuard` an und koennen den Wechsel abbrechen.
   */
  const go = async (path: string) => {
    if (await confirmNavigation()) navigate(path);
  };

  const switchArea = async (target: 'screener' | 'watchdog') => {
    setAreaAnchorEl(null);
    await go(target === 'watchdog' ? '/watchdog/dashboard' : '/scan');
  };

  const handleLogout = async () => {
    closeMenu();
    if (!(await confirmNavigation())) return;
    await logout();
    navigate('/login');
  };

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const navButtonSx = (path: string) => ({
    color: isActive(path) ? colors.lime : colors.white,
    borderBottom: isActive(path) ? `2px solid ${colors.lime}` : '2px solid transparent',
    borderRadius: 0,
    px: 2,
    '&:hover': { color: colors.lime },
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="sticky" elevation={0}>
        <Toolbar sx={{ gap: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              cursor: 'pointer',
              mr: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
            onClick={() => go('/scan')}
          >
            <Box component="span" sx={{ color: colors.lime }}>Tracking</Box>
            Scanner
          </Typography>

          {canSeeWatchdog && (
            <>
              <Button
                onClick={(e) => setAreaAnchorEl(e.currentTarget)}
                endIcon={(
                  <KeyboardArrowDownIcon
                    sx={{ color: colors.silver, transition: 'transform 0.2s', transform: areaMenuOpen ? 'rotate(180deg)' : 'none' }}
                  />
                )}
                sx={{
                  textTransform: 'none',
                  color: colors.white,
                  bgcolor: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: 2,
                  px: 1.25,
                  mr: 2,
                  gap: 1,
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.12)' },
                }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colors.lime }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{areaLabel}</Typography>
              </Button>
              <Menu
                anchorEl={areaAnchorEl}
                open={areaMenuOpen}
                onClose={() => setAreaAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{ paper: { sx: { minWidth: 230, mt: 1, borderRadius: 1.5 } } }}
              >
                <MenuItem selected={area === 'screener'} onClick={() => switchArea('screener')} sx={{ py: 1.25 }}>
                  <ListItemIcon><TravelExploreIcon fontSize="small" /></ListItemIcon>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Domain Screener</Typography>
                    <Typography variant="caption" sx={{ color: colors.black60 }}>Scan · Archiv · Konfiguration</Typography>
                  </Box>
                </MenuItem>
                <MenuItem selected={area === 'watchdog'} onClick={() => switchArea('watchdog')} sx={{ py: 1.25 }}>
                  <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Watchdog</Typography>
                    <Typography variant="caption" sx={{ color: colors.black60 }}>Dashboard · GTM Analyst · Config</Typography>
                  </Box>
                </MenuItem>
              </Menu>
              <Box sx={{ width: '1px', height: 24, bgcolor: 'rgba(255, 255, 255, 0.2)', mr: 2 }} />
            </>
          )}

          {area === 'watchdog' ? (
            <>
              {/* Config bleibt ausgebaut (21.08.), Datei liegt im Repo. */}
              <Button startIcon={<AssessmentIcon />} sx={navButtonSx('/watchdog/dashboard')} onClick={() => go('/watchdog/dashboard')}>
                Scoring
              </Button>
              <Button startIcon={<InsightsIcon />} sx={navButtonSx('/watchdog/gtm-analyst')} onClick={() => go('/watchdog/gtm-analyst')}>
                GTM Analyst
              </Button>
              <Button startIcon={<LanguageIcon />} sx={navButtonSx('/watchdog/domains')} onClick={() => go('/watchdog/domains')}>
                Domains
              </Button>
              <Button startIcon={<AccountTreeIcon />} sx={navButtonSx('/watchdog/graph')} onClick={() => go('/watchdog/graph')}>
                Graph
              </Button>
              <Button startIcon={<PlaylistPlayIcon />} sx={navButtonSx('/watchdog/jobs')} onClick={() => go('/watchdog/jobs')}>
                Jobs
              </Button>
              <Button startIcon={<NotificationsNoneIcon />} sx={navButtonSx('/watchdog/notifications')} onClick={() => go('/watchdog/notifications')}>
                Alarme
              </Button>
            </>
          ) : (
            <>
              <Button startIcon={<SearchIcon />} sx={navButtonSx('/scan')} onClick={() => go('/scan')}>
                Scan
              </Button>
              <Button startIcon={<ArchiveIcon />} sx={navButtonSx('/archive')} onClick={() => go('/archive')}>
                Archiv
              </Button>

              {showConfigurator && (
                <Button startIcon={<SettingsIcon />} sx={navButtonSx('/config')} onClick={() => go('/config')}>
                  Konfigurator
                </Button>
              )}

              {hasRole([UserRole.DIGA, UserRole.ADMIN]) && (
                <Button startIcon={<TuneIcon />} sx={navButtonSx('/config-plus')} onClick={() => go('/config-plus')}>
                  Konfiguration+
                </Button>
              )}
            </>
          )}

          <Box sx={{ flexGrow: 1 }} />

          {user && (
            <>
              <Tooltip title="Fehler melden">
                <IconButton
                  onClick={() => setBugOpen(true)}
                  sx={{ color: colors.silver, mr: 0.5, '&:hover': { color: colors.lime, bgcolor: 'rgba(255, 255, 255, 0.08)' } }}
                >
                  <BugReportIcon />
                </IconButton>
              </Tooltip>

              {/* Aktive Datenbank — nur fuer Admins, dafuer auf jeder Seite, direkt links vom Namen. */}
              {hasRole([UserRole.ADMIN]) && <DbEnvironmentBadge />}

              <Button
                onClick={(e) => setAnchorEl(e.currentTarget)}
                startIcon={<AccountCircleIcon sx={{ color: colors.silver }} />}
                endIcon={(
                  <KeyboardArrowDownIcon
                    sx={{ color: colors.silver, transition: 'transform 0.2s', transform: menuOpen ? 'rotate(180deg)' : 'none' }}
                  />
                )}
                sx={{
                  textTransform: 'none',
                  color: colors.white,
                  gap: 0.5,
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.08)' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: colors.silver }}>{user.name}</Typography>
                  <Chip
                    label={user.role}
                    size="small"
                    sx={{ bgcolor: colors.lime, color: colors.black, fontWeight: 600, pointerEvents: 'none' }}
                  />
                </Box>
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={closeMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { minWidth: 190, mt: 1, borderRadius: 1.5 } } }}
              >
                {hasRole([UserRole.ADMIN]) && (
                  <MenuItem onClick={() => { closeMenu(); void go('/admin'); }} sx={{ py: 1.25 }}>
                    <ListItemIcon><AdminPanelSettingsIcon fontSize="small" /></ListItemIcon>
                    Admin
                  </MenuItem>
                )}
                {hasRole([UserRole.ADMIN]) && <Divider sx={{ my: 0.5 }} />}
                <MenuItem onClick={handleLogout} sx={{ py: 1.25 }}>
                  <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>

      <BugReportDialog open={bugOpen} onClose={() => setBugOpen(false)} />

      <Container maxWidth="lg" sx={{ flex: 1, py: 4 }}>
        <Outlet />
      </Container>

      <Box component="footer" sx={{ bgcolor: colors.black, color: colors.black60, py: 2, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ fontFamily: "'Amiri', serif", fontStyle: 'italic' }}>
          ambition by mso digital
        </Typography>
      </Box>
    </Box>
  );
}
